// Aidea Wizard "Extract Text": turn baked-in text on the selected image into editable
// BasicText boxes. The middleware extract-text job (Florence-2 OCR) returns one text
// region per detection plus a cleaned background with the text erased. Applying the
// result swaps the source image for the cleaned background and drops one editable text
// box per detected region (positions are % of the source image → mapped onto canvas).
//
// NOTE: the job is fired from the ImageAnimatorModal (like remove-object and the other
// image-editing features) — NOT auto-run + auto-applied on click. The modal polls the
// job, previews the cleaned background, and the user presses "Accept" to apply. This
// thunk is the apply step; EditorApp.onAcceptAnimation dispatches it (and JobWidgetHost
// mirrors the same logic for approve-from-widget).
import { updatePluginToolbar, addBox } from "./actions.es6";
import { createBox } from "./common_tools";

function pctNum(v) {
  const n = parseFloat(String(v == null ? "" : v).replace("%", ""));
  return isFinite(n) ? n : 0;
}

function escapeHtml(s) {
  return (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Walk up parents to the owning navItem (same helper as aideaSmartElements). */
function resolveOwningNavId(boxesById, navItemsById, startBoxId) {
  let cursor = startBoxId;
  for (let i = 0; i < 32 && cursor != null; i++) {
    if (navItemsById && Object.prototype.hasOwnProperty.call(navItemsById, cursor)) {
      return cursor;
    }
    const b = boxesById && boxesById[cursor];
    if (!b) return cursor;
    cursor = b.parent;
  }
  return cursor;
}

function getPresent(state) {
  // Store shape matches aideaSmartElements: live slice lives under undoGroup.present.
  return state && state.undoGroup && state.undoGroup.present ? state.undoGroup.present : state;
}

/**
 * Apply an extract-text job result onto the canvas: swap the source box image for the
 * cleaned background, then create one editable BasicText box per detected region.
 *
 * @param {{url?: string, textBoxes?: Array}} result — middleware extract-text result.
 * @param {string} boxId — the source image box the job ran on.
 * @param {string} [navId] — the owning scene; resolved from boxId when omitted.
 */
export function applyExtractedTextLayers(result, boxId, navId) {
  return (dispatch, getState) => {
    if (!result || !boxId) return;
    const present = getPresent(getState());
    const boxesById = present.boxesById || {};
    const box = boxesById[boxId];
    const tb = (present.pluginToolbarsById || {})[boxId];
    if (!box || !tb) {
      console.warn("[ExtractText] apply skipped: box not found", boxId);
      return;
    }
    const owningNavId = navId || resolveOwningNavId(boxesById, present.navItemsById, boxId);

    // 1) Swap the source image for the cleaned background (text erased).
    if (result.url) {
      dispatch(updatePluginToolbar(boxId, "main", "state", "url", result.url));
    }

    const textBoxes = Array.isArray(result.textBoxes)
      ? result.textBoxes
      : Array.isArray(result.details && result.details.textBoxes)
        ? result.details.textBoxes
        : [];
    if (!textBoxes.length) return;

    // Source box geometry (percent of canvas). Region coords are percent of the
    // IMAGE, so map: canvas = source origin + region · source size.
    const sx = pctNum(box.position && box.position.x);
    const sy = pctNum(box.position && box.position.y);
    const sw = pctNum(tb.structure && tb.structure.width) || 100;
    const sh = pctNum(tb.structure && tb.structure.height) || 100;

    // The caption lives on the timeline exactly WHEN its source image does — it
    // enters when the image enters and leaves when the image leaves. Inherit the
    // source image box's timeline (start/end frame only, not visibility or the
    // interactions array) so the extracted text doesn't default to frame 0.
    // boxCreator reads initialParams.timeline, so passing it in the createBox ids
    // is enough for the new box to own the same lifetime as its image.
    const srcTimeline = box.timeline && typeof box.timeline === "object" ? box.timeline : null;
    const inheritedTimeline = srcTimeline
      ? {
          startFrame: Number.isFinite(srcTimeline.startFrame) ? srcTimeline.startFrame : 0,
          ...(Number.isFinite(srcTimeline.endFrame) && { endFrame: srcTimeline.endFrame }),
        }
      : null;

    const addBoxFn = (...args) => dispatch(addBox(...args));

    textBoxes.forEach((t, i) => {
      const cx = sx + (pctNum(t.x) * sw) / 100;
      const cy = sy + (pctNum(t.y) * sh) / 100;
      const cw = Math.max(4, (pctNum(t.width) * sw) / 100);

      const html =
        `<p style="margin:0;"><span style="font-size:${t.fontSize};color:${t.color};` +
        `font-family:Arial,Helvetica,sans-serif;font-weight:700;line-height:1.25;">` +
        `${escapeHtml(t.content)}</span></p>`;

      const id = `bo-${Date.now()}-txt-${i}-${Math.random().toString(36).slice(2, 5)}`;
      createBox(
        {
          id,
          parent: owningNavId,
          page: owningNavId,
          container: 0,
          position: {
            x: `${cx.toFixed(2)}%`,
            y: `${cy.toFixed(2)}%`,
            type: "absolute",
          },
          widthPercent: `${cw.toFixed(2)}%`,
          initialState: { __text: encodeURIComponent(html + "\n") },
          ...(inheritedTimeline && { timeline: inheritedTimeline }),
        },
        "BasicText",
        true,
        addBoxFn,
        boxesById,
        // Zero the plugin's default 7px padding so the text's top-left sits exactly
        // at the region origin (the default inset was the small visible shift).
        { padding: 0 }
      );
    });
  };
}
